import { useEffect, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";
import ImageEditorModal from "./ImageEditorModal";

const FIELDS = [
    { name: "registration_url", label: "Link Formulir Pendaftaran Member" },
    { name: "whatsapp", label: "Link WhatsApp (https://wa.me/...)" },
    { name: "email", label: "Email Klub" },
    { name: "instagram", label: "Link Instagram" },
    { name: "tiktok", label: "Link TikTok" },
    { name: "youtube", label: "Link YouTube" },
    { name: "facebook", label: "Link Facebook" },
    { name: "banner_text", label: "Teks Banner Pengumuman (kosongkan untuk menyembunyikan)" },
    { name: "banner_link", label: "Link Banner (opsional)" },
    { name: "founded_year", label: "Tahun Berdiri Klub (mis. 2019) — tampil di Hero" },
];

const PAYMENT_FIELDS = [
    { name: "registration_fee", label: "Biaya Pendaftaran Siswa Baru (Rp)", type: "number" },
    { name: "dues_amount_default", label: "Iuran Bulanan Bawaan (Rp) — nominal per atlet bisa diganti di tab Iuran", type: "number" },
    { name: "payment_shopeepay_number", label: "Nomor ShopeePay Penerima Pembayaran (mis. 08523000454)" },
    { name: "notification_email", label: "Email Notifikasi Admin (pengingat ulang tahun atlet, rekap latihan, permintaan reset password) — WAJIB alamat email asli yang aktif" },
];

const HERO_SLOTS = Array.from({ length: 10 }, (_, index) => index);

export default function SettingsTab() {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState("");
    const [editor, setEditor] = useState(null);

    const pickFile = (fieldName, file, heroIndex, aspect) => {
        if (!file) return;
        setEditor({ src: URL.createObjectURL(file), fieldName, heroIndex, aspect });
    };

    const editExisting = async (url, fieldName, heroIndex, aspect) => {
        try {
            const res = await fetch(fileUrl(url));
            const blob = await res.blob();
            setEditor({ src: URL.createObjectURL(blob), fieldName, heroIndex, aspect });
        } catch {
            toast.error("Gagal memuat gambar untuk diedit");
        }
    };

    const closeEditor = () => {
        if (editor?.src) URL.revokeObjectURL(editor.src);
        setEditor(null);
    };

    const onEditorSave = async (blob) => {
        const { fieldName, heroIndex } = editor;
        closeEditor();
        const file = new File([blob], "foto.jpg", { type: "image/jpeg" });
        await uploadImage(fieldName, file, heroIndex);
    };

    const setHeroImage = (index, url) => {
        setForm((prev) => {
            const images = [...(prev.hero_image_urls || [])];
            images[index] = url;
            return { ...prev, hero_image_urls: images };
        });
    };

    const uploadImage = async (fieldName, file, heroIndex = null) => {
        setUploading(fieldName);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/admin/upload", fd);
            if (heroIndex === null) setForm((prev) => ({ ...prev, [fieldName]: r.data.url }));
            else setHeroImage(heroIndex, r.data.url);
            toast.success("Gambar terunggah");
        } catch {
            toast.error("Gagal mengunggah gambar");
        } finally {
            setUploading("");
        }
    };

    useEffect(() => {
        api.get("/content")
            .then((r) => {
                const settings = r.data.settings || {};
                const heroImages = Array.isArray(settings.hero_image_urls) && settings.hero_image_urls.length
                    ? settings.hero_image_urls
                    : [settings.hero_image_url].filter(Boolean);
                setForm({ ...settings, hero_image_urls: heroImages });
            })
            .catch(() => toast.error("Gagal memuat pengaturan"));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const heroImages = (form.hero_image_urls || []).map((url) => url?.trim()).filter(Boolean).slice(0, 10);
            await api.put("/admin/settings", { ...form, hero_image_urls: heroImages, hero_image_url: heroImages[0] || "" });
            setForm((prev) => ({ ...prev, hero_image_urls: heroImages, hero_image_url: heroImages[0] || "" }));
            toast.success("Pengaturan tersimpan");
        } catch {
            toast.error("Gagal menyimpan");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="settings-tab">
            <h2 className="font-display uppercase text-2xl text-neutral-900 mb-6">Pengaturan Link</h2>
            <form onSubmit={save} className="max-w-3xl space-y-5" data-testid="settings-form">
                <div className="border border-racing-gold/50 bg-racing-gold/5 p-5 space-y-5" data-testid="settings-payment-section">
                    <h3 className="font-display uppercase text-lg text-neutral-900">Pembayaran & Pengingat WhatsApp</h3>
                    <p className="text-xs text-racing-muted -mt-3">Pembayaran dipakai orang tua di portal (uang masuk langsung ke ShopeePay klub). Pengingat ulang tahun terkirim otomatis ke nomor Anda setiap pagi.</p>
                    {PAYMENT_FIELDS.map((f) => (
                        <div key={f.name}>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">{f.label}</label>
                            <input
                                type={f.type || "text"}
                                min={0}
                                value={form[f.name] ?? ""}
                                onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value || 0) : e.target.value })}
                                data-testid={`settings-field-${f.name}`}
                                className="w-full bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red"
                            />
                        </div>
                    ))}
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Gambar QRIS ShopeePay (ditampilkan ke orang tua saat membayar)</label>
                        {form.payment_qris_image && (
                            <div className="mb-3 flex items-center gap-4">
                                <img src={fileUrl(form.payment_qris_image)} alt="QRIS" className="h-32 border border-racing-line object-contain bg-white" data-testid="settings-qris-preview" />
                                <button type="button" onClick={() => editExisting(form.payment_qris_image, "payment_qris_image", null, 1 / 1)} data-testid="settings-qris-edit" className="text-[10px] uppercase tracking-[0.2em] text-racing-red hover:text-racing-redhover">Edit Gambar</button>
                            </div>
                        )}
                        <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickFile("payment_qris_image", f, null, 1 / 1); }} data-testid="settings-file-qris" className="w-full text-xs text-racing-muted" />
                        <input type="text" value={form.payment_qris_image ?? ""} onChange={(e) => setForm({ ...form, payment_qris_image: e.target.value })} placeholder="atau tempel URL gambar QRIS" data-testid="settings-field-payment_qris_image" className="mt-2 w-full bg-white border border-racing-line px-3 py-2.5 text-sm" />
                    </div>
                    <label className="flex items-start gap-3 border border-racing-line bg-white p-4 cursor-pointer" data-testid="settings-midtrans-toggle-wrap">
                        <input
                            type="checkbox"
                            checked={Boolean(form.midtrans_disabled)}
                            onChange={(e) => setForm({ ...form, midtrans_disabled: e.target.checked })}
                            data-testid="settings-field-midtrans_disabled"
                            className="mt-0.5 w-4 h-4 accent-racing-red"
                        />
                        <span>
                            <span className="block text-sm font-medium text-neutral-900">Nonaktifkan pembayaran online otomatis (Midtrans) sementara</span>
                            <span className="block text-xs text-racing-muted mt-1">Jika dicentang, orang tua membayar iuran/pendaftaran via QRIS ShopeePay di atas lalu wajib mengunggah bukti bayar — admin/keuangan yang mengonfirmasi. Dukungan dana publik tidak terpengaruh.</span>
                        </span>
                    </label>
                </div>
                {FIELDS.map((f) => (
                    <div key={f.name}>
                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">{f.label}</label>
                        <input
                            type="text"
                            value={form[f.name] ?? ""}
                            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                            data-testid={`settings-field-${f.name}`}
                            className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm text-neutral-900 focus:outline-none focus:border-racing-red transition-colors"
                        />
                    </div>
                ))}

                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Logo Website (tampil di navigasi)</label>
                    {form.logo_url ? (
                        <div className="mb-2 flex items-end gap-3">
                            <img src={fileUrl(form.logo_url)} alt="Logo" className="h-20 object-contain border border-racing-line bg-white p-2" />
                            <button type="button" onClick={() => editExisting(form.logo_url, "logo_url", null, 1 / 1)} data-testid="settings-edit-logo_url" className="border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">
                                Edit Foto
                            </button>
                        </div>
                    ) : null}
                    <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickFile("logo_url", f, null, 1 / 1); }} data-testid="settings-file-logo_url" className="w-full text-xs text-racing-muted" />
                    {uploading === "logo_url" && <p className="mt-1 text-xs text-racing-red">Mengunggah...</p>}
                    <input type="text" value={form.logo_url ?? ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="atau tempel URL gambar" data-testid="settings-field-logo_url" className="mt-2 w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                </div>

                <section className="border border-racing-line bg-racing-surface p-5" data-testid="hero-carousel-settings">
                    <h3 className="font-display uppercase text-xl mb-2">Foto Hero Carousel</h3>
                    <p className="text-sm text-racing-muted mb-5">Isi sampai 10 foto. Foto akan tampil jelas di panel kanan dan bisa digeser pengunjung.</p>
                    <div className="grid md:grid-cols-2 gap-5">
                        {HERO_SLOTS.map((slot) => {
                            const url = form.hero_image_urls?.[slot] || "";
                            return (
                                <div key={slot} className="bg-white border border-racing-line p-4" data-testid={`hero-slot-${slot}`}>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Hero {slot + 1}</label>
                                    {url ? (
                                        <div className="mb-3">
                                            <img src={fileUrl(url)} alt={`Hero ${slot + 1}`} className="h-28 w-full object-cover border border-racing-line" />
                                            <button type="button" onClick={() => editExisting(url, `hero_image_${slot + 1}`, slot, 16 / 9)} data-testid={`settings-edit-hero-${slot}`} className="mt-2 border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">
                                                Edit Foto
                                            </button>
                                        </div>
                                    ) : <div className="mb-3 h-28 border border-dashed border-racing-line" />}
                                    <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickFile(`hero_image_${slot + 1}`, f, slot, 16 / 9); }} data-testid={`settings-file-hero-${slot}`} className="w-full text-xs text-racing-muted" />
                                    {uploading === `hero_image_${slot + 1}` && <p className="mt-1 text-xs text-racing-red">Mengunggah...</p>}
                                    <input type="text" value={url} onChange={(e) => setHeroImage(slot, e.target.value)} placeholder="atau tempel URL gambar" data-testid={`settings-field-hero-${slot}`} className="mt-2 w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                                </div>
                            );
                        })}
                    </div>
                </section>

                <button type="submit" disabled={saving} data-testid="settings-save-button" className="bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] px-8 py-3.5 transition-colors duration-300">
                    {saving ? "Menyimpan..." : "Simpan Pengaturan"}
                </button>
            </form>

            <ImageEditorModal
                open={!!editor}
                imageSrc={editor?.src}
                aspect={editor?.aspect || 16 / 9}
                title="Edit Gambar"
                onCancel={closeEditor}
                onSave={onEditorSave}
            />
        </div>
    );
}
